/**
 * Uniform spatial hash for broadphase queries.
 *
 * Used by World to:
 *   - find food pellets near a snake head (eat lookup),
 *   - find snake heads near another snake's body (collision lookup).
 *
 * Items are addressed by integer id. We never store the items themselves —
 * the caller iterates the returned ids and resolves them through its own
 * map/array. This keeps the grid GC-friendly across frames.
 */
export class SpatialHash {
  private readonly cellSize: number;
  private readonly buckets = new Map<number, number[]>();

  constructor(cellSize: number) {
    this.cellSize = cellSize;
  }

  clear(): void {
    // Reuse the bucket arrays — only truncate them, don't drop the Map keys.
    for (const arr of this.buckets.values()) arr.length = 0;
  }

  /** Hash a cell coordinate pair into a single integer key. */
  private key(cx: number, cy: number): number {
    // Cantor-like pairing, signed-safe. Map ints to 16-bit halves.
    const a = (cx + 32768) & 0xffff;
    const b = (cy + 32768) & 0xffff;
    return (a << 16) | b;
  }

  insert(id: number, x: number, y: number): void {
    const cx = Math.floor(x / this.cellSize);
    const cy = Math.floor(y / this.cellSize);
    const k = this.key(cx, cy);
    let arr = this.buckets.get(k);
    if (!arr) {
      arr = [];
      this.buckets.set(k, arr);
    }
    arr.push(id);
  }

  /** Append every id within `radius` of (x,y) to `out`. Returns out. */
  queryCircle(x: number, y: number, radius: number, out: number[]): number[] {
    const cs = this.cellSize;
    const minCx = Math.floor((x - radius) / cs);
    const maxCx = Math.floor((x + radius) / cs);
    const minCy = Math.floor((y - radius) / cs);
    const maxCy = Math.floor((y + radius) / cs);
    for (let cy = minCy; cy <= maxCy; cy++) {
      for (let cx = minCx; cx <= maxCx; cx++) {
        const arr = this.buckets.get(this.key(cx, cy));
        if (!arr) continue;
        for (let i = 0; i < arr.length; i++) out.push(arr[i]!);
      }
    }
    return out;
  }
}
